import styled, { css } from 'styled-components';
import { Drawer } from '@insly/qmt-reactjs-ui-lib';
import { isIframe } from 'utils';
import {
  ContactsDataBlock,
  ContactsDataBlockTitle,
  ContactsItem,
} from 'styles/common';
import {
  DrawerBodyStyled,
  DrawerCloseIcon,
  DrawerDataFormControls,
  DrawerHeaderRow,
  DrawerHeaderRowTitle,
  DrawerSectionTitle,
  DrawerUserForm,
  DrawerUserFormAdditional,
  DrawerUserFormAdditionalRow,
} from 'app/Components/common/Drawer/styles';
import { CEIDGWrapper, DrawerUserType } from 'app/Pages/Clients/drawer/styles';
import { ButtonAddContact } from 'app/Pages/Clients/common/styles';
import {
  ButtonAddItem,
  ContactsControls,
  ContactsItemWrapper,
  ContactsItemsWrapper,
  ContactsRemoveIcon,
  IconPlus,
} from 'app/Pages/Clients/profile/body/styles';
import { DrawerParticipantRoleWrapper as MotorRoleWrapper } from 'app/Pages/QuoteMotor/Profile/body/Participants/Drawer/common/ParticipantRoles/styles';
import { DrawerParticipantRoleWrapper as BorderRoleWrapper } from 'app/Pages/QuoteBorder/Profile/body/Participants/Drawer/common/ParticipantRoles/styles';
import { ContentBodyRow } from 'app/Pages/Quote/Profile/body/styles';
import { ShowMoreStyled } from 'app/Components/common/ShowMore/styles';

export const PARTICIPANT_DRAWER_WIDTH = '460px';

const FIELD_MIN_HEIGHT = '46px';
// Rendered height of a labelled field (label + value); every field box and the delete button use it
const FIELD_HEIGHT = '52px';
const FIELD_GAP = '12px';
const TEXT_OFF = '#C4C4C4';
const CLOSE_BG = '#F0F0F0';
const CLOSE_BG_HOVER = '#E2E2E2';

// Content look shared by every customer drawer (quotes, Clients, APK): header, fields, sections, contacts, address, Save
export const customerDrawerContentStyles = css`
  // Header
  ${DrawerHeaderRow} {
    align-items: center;
    padding: 20px 24px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.strokeB};
  }

  ${DrawerHeaderRowTitle} {
    font-family: ${({ theme }) => theme.font.bold};
    font-size: 17px;
  }

  ${DrawerCloseIcon} {
    width: 28px;
    height: 28px;
    padding: 7px;
    border-radius: 50%;
    background-color: ${CLOSE_BG};
    transition: background-color 0.2s;

    &:hover {
      background-color: ${CLOSE_BG_HOVER};
    }
  }

  // Scrollable body
  ${DrawerBodyStyled} {
    display: block;
    margin: 0;
    padding: 18px 24px 8px;
  }

  ${DrawerUserForm} {
    padding-bottom: 0;
  }

  // Keep the last controls off the panel edge while the Save footer is hidden. Only the last block:
  // the edit drawer renders the contact sections after the form, and padding there splits the sections apart
  ${DrawerBodyStyled} > :last-child:not(:has(${DrawerDataFormControls})) {
    padding-bottom: 24px;
  }

  // The sticky footer and the scrollbar replace the "show more" hint here
  ${ShowMoreStyled} {
    display: none;
  }

  ${DrawerUserFormAdditional} {
    margin-top: 0;
  }

  ${DrawerUserFormAdditionalRow} {
    margin-bottom: 0;
  }

  // Checkboxes: roles, citizenship
  .qmt-ui-checkbox-picker {
    width: 14px;
    height: 14px;
    border-radius: 3px;

    &:after {
      width: 12px;
      height: 12px;
      background-size: 8px;
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  .qmt-ui-checkbox-label {
    font-size: 12px;
  }


  .qmt-ui-checkbox--disabled.qmt-ui-checkbox--checked {
    opacity: 1;

    .qmt-ui-checkbox-picker {
      background-color: ${TEXT_OFF};
      border-color: ${TEXT_OFF};
    }
  }

  ${MotorRoleWrapper},
  ${BorderRoleWrapper} {
    flex-wrap: nowrap;
    gap: 18px;
    padding: 0;

    .qmt-ui-checkbox {
      margin: 0;
      white-space: nowrap;
    }

    .qmt-ui-checkbox--checked .qmt-ui-checkbox-label {
      font-family: ${({ theme }) => theme.font.bold};
    }
  }

  // Client type toggle
  ${DrawerUserType} {
    padding: 14px 0 0;
  }

  ${ContentBodyRow} {
    margin: 14px 0 0;
    padding: 0;
  }

  // CEIDG lookup between the sole trader REGON/NIP and the company fields, spaced like a field
  ${CEIDGWrapper} {
    padding: 0;
    margin-bottom: ${FIELD_GAP};
  }

  // Section labels without full-width separators; the gap between sections lives on the block
  ${ContactsDataBlock} {
    margin: 20px 0 0;
    padding: 0;
    border: none;
  }

  ${ContactsDataBlockTitle} {
    margin: 0 0 10px;
    font-family: ${({ theme }) => theme.font.bold};
    font-size: 11px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  ${DrawerSectionTitle} {
    margin-top: 16px;
  }

  // Fields: separate bordered boxes instead of one joined form group
  .qmt-ui-form-group {
    margin-top: 0;
    border: none;
    background: transparent;
  }

  .qmt-ui-form-group__row {
    flex-wrap: wrap;
    gap: ${FIELD_GAP};
    height: auto;
    margin-bottom: ${FIELD_GAP};
    border: none;
  }

  .qmt-ui-form-group__col {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1 1 100% !important;
    min-width: 0;
    height: auto;
    min-height: ${FIELD_HEIGHT};
    border: 1px solid ${({ theme }) => theme.colors.strokeA} !important;
    border-radius: 5px;
    background-color: ${({ theme }) => theme.colors.white};

    > * {
      width: 100%;
    }
  }

  .qmt-ui-form-group__col .qmt-ui-select,
  .qmt-ui-form-group__col .qmt-ui-dropdown {
    height: auto;
  }

  .qmt-ui-input,
  .qmt-ui-dropdown__picker {
    height: auto;
    min-height: ${FIELD_MIN_HEIGHT};
    padding: 7px 12px;
  }

  // Search icon (e.g. PESEL) in the same spot as the select chevron: 24px, 12px from the right, on the value line
  .qmt-ui-search-input [class*='IconAdditional'] {
    align-self: flex-end;
    width: 24px;
    height: 24px;
    margin: 0 0 2px 8px;
  }

  .qmt-ui-input input,
  .qmt-ui-dropdown__picker input,
  .qmt-ui-select__value {
    font-size: 13px;
  }

  // Required marker right after the label text (lib pushes it to the field's top-right corner)
  [class*='RequiredStyled'],
  .qmt-ui-select--required [class*='LabelCustom'] > span {
    display: inline;
    margin: 0 0 0 2px !important;
    font-size: inherit;
    line-height: inherit;
  }

  .qmt-ui-input .qmt-ui-svg-icon:first-child,
  .qmt-ui-dropdown__picker .qmt-ui-svg-icon:first-child {
    margin-right: 18px;
  }

  // Contacts: phone (3) + type (1) + remove button
  ${ContactsItem} {
    min-width: 0;
  }

  ${ContactsItemsWrapper} {
    display: flex;
    flex-direction: column;
    gap: ${FIELD_GAP};
    margin-bottom: 12px;
  }

  ${ContactsItemWrapper} {
    align-items: flex-start;
    gap: ${FIELD_GAP};
    margin: 0;
  }

  // Phone/email rows only: the edit drawer renders addresses inside the same wrapper
  ${ContactsItemsWrapper} .qmt-ui-form-group__row:has(.qmt-ui-input--value) {
    flex-wrap: nowrap;
    margin-bottom: 0;
  }

  ${ContactsItemsWrapper} .qmt-ui-form-group__row:has(.qmt-ui-input--value) .qmt-ui-form-group__col-1 {
    flex: 3 1 0 !important;
  }

  // At 460px the 3:1 split leaves the type under its max width, so keep it at the max to fit "Komórkowy"
  ${ContactsItemsWrapper} .qmt-ui-form-group__row:has(.qmt-ui-input--value) .qmt-ui-form-group__col-2 {
    flex: 0 1 106px !important;
    min-width: 76px;
    max-width: 106px;

    .qmt-ui-dropdown__picker {
      padding: 7px 4px 7px 10px;
    }

    .qmt-ui-dropdown__picker > :first-child {
      flex: 1 1 auto;
      justify-content: flex-start;
      min-width: 0;
    }

    [class*='InputWrapper'] {
      min-width: 0;
      overflow: hidden;
    }

    [class*='LabelCustom'],
    .qmt-ui-select__value {
      display: block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .qmt-ui-dropdown__picker .qmt-ui-svg-icon:last-child {
      width: 14px;
      height: 14px;
      margin: 0;
      flex-shrink: 0;
    }
  }

  // Icon only, in a field-high box so it stays centred on the row
  ${ContactsRemoveIcon} {
    position: static;
    flex-shrink: 0;
    width: 40px;
    height: ${FIELD_HEIGHT};
    margin: 0;
    padding: 16px 10px;
    border: none;
    stroke: ${({ theme }) => theme.colors.black};
    cursor: pointer;
    transition: stroke 0.2s;

    &:hover {
      stroke: ${({ theme }) => theme.colors.error};
    }
  }

  ${ContactsControls} {
    display: flex;
    justify-content: flex-start;
    gap: 20px;
    margin: 0;
  }

  // Section Save in the edit drawer (phone/email, address, contact person): below the add links and, like
  // every ContactsControls here, left-aligned so the floating support widget in the bottom-right can't cover it
  ${ContactsControls}.contacts-save-controls {
    margin: 12px 0;
  }

  ${ButtonAddContact},
  ${ButtonAddItem} {
    display: flex;
    align-items: center;
    align-self: flex-start;
    flex: none;
    gap: 4px;
    width: auto;
    height: auto;
    margin: 0;
    padding: 0;
    background: none;
    border: none;
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 12px;
    color: ${({ theme }) => theme.colors.black};

    &:hover {
      background: none;
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  ${ButtonAddContact}:before {
    content: '+';
    font-size: 16px;
    line-height: 1;
    color: ${({ theme }) => theme.colors.primary};
  }

  ${ButtonAddItem} {
    flex-direction: row-reverse;

    ${IconPlus} {
      width: 14px;
      height: 14px;
      margin: 0;
      stroke: ${({ theme }) => theme.colors.primary};
    }
  }

  // Address: narrow postcode + wide city, then the widest street with small house and apartment numbers.
  // City and street switch between input and select after a postcode lookup, so both variants are covered.
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--zip_code),
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--postalCode) {
    flex: 1 1 0 !important;
  }

  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--city),
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-select--city) {
    flex: 2 1 0 !important;
  }

  ${ContactsItem} .qmt-ui-form-group__row:has(.qmt-ui-input--street, .qmt-ui-select--street) {
    flex-wrap: nowrap;
  }

  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--street),
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-select--street) {
    flex: 2 1 0 !important;
  }

  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--house),
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--room),
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--houseNumber),
  ${ContactsItem} .qmt-ui-form-group__col:has(.qmt-ui-input--flatNumber) {
    flex: 1 1 0 !important;
  }

  ${ContactsItem} .qmt-ui-input.address-house-nr:after {
    display: none;
  }

  // Sticky footer with a single primary Save, left-aligned clear of the floating support widget
  ${DrawerDataFormControls} {
    justify-content: flex-start;
    position: sticky;
    bottom: -8px;
    // 0, not 1: still above the scrolling form (later in the DOM) but under LoadingOverlay (z-index 1) while saving
    z-index: 0;
    margin: 18px -24px -8px;
    padding: 14px 24px;
    background-color: ${({ theme }) => theme.colors.white};

    // Only the Save (last) button is primary; a Cancel next to it keeps its own outlined look
    .qmt-ui-button:last-child {
      background-color: ${({ theme }) => theme.colors.buttonPreset.primary.bg};
      color: ${({ theme }) => theme.colors.buttonPreset.primary.text};

      &:hover {
        background-color: ${({ theme }) => theme.colors.buttonPreset.primary.hover};
        color: ${({ theme }) => theme.colors.buttonPreset.primary.textHover};
      }
    }
  }
`;

export const DrawerParticipantsStyled = styled(Drawer)`
  && {
    // Evaluated at render: this module can load before utils when imported from the Clients drawer
    top: ${() => isIframe() ? 0 : '55px'};
    right: 0;
    left: auto;
    display: flex;
    flex-direction: column;
    width: ${PARTICIPANT_DRAWER_WIDTH};
    max-width: 92vw;
    padding: 0;
    background-color: ${({ theme }) => theme.colors.white};
    transform: translateX(100%);
    transition: transform 0.28s cubic-bezier(.2, .7, .3, 1), box-shadow 0.28s;
    z-index: 2;
  }

  // Shadow only while open, otherwise it bleeds into the page from the off-screen panel
  &&.qmt-ui-drawer--show {
    transform: translateX(0);
    box-shadow: -12px 0 32px rgba(0, 0, 0, 0.14);
  }

  ${customerDrawerContentStyles}
`;
